/**
 * Post-match ball editor.
 *
 * Reachable from the matches list ("Edit balls") and the scorecard ("Edit balls").
 * Lets the operator pick an innings, browse every ball, and open the existing
 * EditBallDialog (same editor used live) to fix a saved ball — runs, wicket,
 * extras, players, spatial, commentary.
 *
 * Source data:
 *  - Match scorecard endpoint for innings + playing XIs
 *  - listInningsBalls for the per-ball table within the picked innings
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, Pencil, RotateCw } from "lucide-react";
import { getMatchScorecard } from "../../../api/matches.api";
import { listInningsBalls } from "../../../api/ballbyball.api";
import EditBallDialog from "../../../components/ballbyball/EditBallDialog";
import { fireObserveOnSave as fireAiObserveOnSave } from "../../../utils/aiObservationStash";
import { formatBallReference } from "../../../utils/hundredDisplay";

export default function EditBalls() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Deep-link query params, e.g. coming from the QC screen's Edit button:
  //   /cricket/matches/<id>/edit-balls?ball=<ballId>&innings=<inningsId>
  // We use these to pre-select the right innings tab and auto-open the
  // EditBallDialog on the right delivery — the operator lands directly
  // in the editor without manually navigating.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkBallId    = searchParams.get("ball");
  const deepLinkInningsId = searchParams.get("innings");

  const [scorecard, setScorecard] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeInnings, setActiveInnings] = useState(null);
  const [balls, setBalls] = useState([]);
  const [ballsLoading, setBallsLoading] = useState(false);
  const [editingBallId, setEditingBallId] = useState(null);
  const [filter, setFilter] = useState("");

  // Load the scorecard once for innings list + playing XIs
  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMatchScorecard(id)
      .then((res) => {
        if (!alive) return;
        setScorecard(res.data);
        const innings = res.data?.innings || [];
        // If we arrived from a deep link with a specific innings, jump
        // to that tab; otherwise default to the first innings.
        if (innings.length) {
          const target = deepLinkInningsId
            ? innings.find((inn) => inn.id === deepLinkInningsId)
            : null;
          setActiveInnings(target || innings[0]);
        }
      })
      .catch((e) =>
        setError(e?.response?.data?.detail || e.message || "Failed to load match"),
      )
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, deepLinkInningsId]);

  // Once balls load for the active innings, if a deep-link ball ID is
  // present and we haven't opened it yet, auto-open the dialog. Then
  // strip the query params so a refresh doesn't reopen.
  useEffect(() => {
    if (!deepLinkBallId) return;
    if (editingBallId) return;
    if (!balls.length) return;
    const found = balls.find((b) => b.id === deepLinkBallId);
    if (found) {
      setEditingBallId(deepLinkBallId);
      // Clean the URL so subsequent navigation / refresh doesn't re-fire.
      setSearchParams({}, { replace: true });
    }
  }, [deepLinkBallId, balls, editingBallId, setSearchParams]);

  // Load balls for the active innings whenever it changes
  const fetchBalls = (inningsId) => {
    if (!inningsId) return;
    setBallsLoading(true);
    listInningsBalls(inningsId)
      .then((res) => setBalls(res.data?.results || res.data || []))
      .catch(() => setBalls([]))
      .finally(() => setBallsLoading(false));
  };
  useEffect(() => {
    if (activeInnings?.id) fetchBalls(activeInnings.id);
  }, [activeInnings?.id]);

  // Build a permissive player roster from both XIs so the editor's dropdowns
  // resolve every player who appeared in the match.
  //
  // Field mapping: MatchScorecardView serialises each PlayingXI row as
  //   {player_id, player_name, batting_position, is_captain, is_wicket_keeper,
  //    substitution_role}
  // — there's no separate first_name / last_name / nick_name. Reading
  // p.player__first_name (the old shape) returned undefined for every row, so
  // playerDisplayName fell through to p.id and the operator saw raw UUIDs in
  // every dropdown on completed matches.
  const players = useMemo(() => {
    if (!scorecard) return { batters: [], bowlers: [], fielders: [] };
    const xi = scorecard.playing_xi || {};
    const all = [];
    Object.values(xi).forEach((rows) => {
      (rows || []).forEach((p) => {
        const pid = p.player_id || p.id;
        if (!pid) return;
        all.push({
          id: pid,
          name: p.player_name || p.name || "",
          nick_name: p.nick_name || null,
          first_name: p.first_name || "",
          last_name: p.last_name || "",
        });
      });
    });
    return { batters: all, bowlers: all, fielders: all };
  }, [scorecard]);

  const playerName = (id) => {
    if (!id) return "—";
    const p = players.batters.find((x) => x.id === id);
    if (!p) return id;
    return p.nick_name || p.name || `${p.first_name} ${p.last_name}`.trim() || id;
  };

  const filteredBalls = useMemo(() => {
    if (!filter.trim()) return balls;
    const q = filter.toLowerCase();
    return balls.filter((b) => {
      const blob = [
        b.over_number,
        b.ball_in_over,
        b.runs_off_bat,
        b.wicket_type,
        b.extras_type,
        b.commentary,
        playerName(b.batter_id || b.striker_id),
        playerName(b.bowler_id),
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(q);
    });
  }, [balls, filter, players]);

  if (loading) {
    return <Shell><div className="text-text-secondary">Loading match…</div></Shell>;
  }
  if (error) {
    return <Shell><div className="text-rose-400 text-sm">{error}</div></Shell>;
  }
  if (!scorecard) return null;

  const { match, match_type: matchType, team1, team2, innings } = scorecard;

  return (
    <Shell>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <button
          onClick={() => navigate("/cricket/matches")}
          className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="w-4" /> Back to matches
        </button>
        <Link
          to={`/cricket/matches/${id}/scorecard`}
          className="text-xs px-3 py-1.5 rounded bg-bg-secondary hover:bg-bg-main text-text-primary border border-border-default transition-colors"
        >
          View scorecard
        </Link>
      </div>

      <div className="mb-4">
        <h1 className="text-2xl font-bold text-text-primary">
          {team1?.name} vs {team2?.name}
        </h1>
        <p className="text-text-secondary text-sm mt-0.5">
          Post-match ball editor · {match?.match_type_name || ""} · state {match?.state}
        </p>
      </div>

      {/* Innings tabs */}
      {innings && innings.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-2 border-b border-border-default pb-2">
          {innings.map((inn, idx) => {
            const isActive = activeInnings?.id === inn.id;
            const battingTeamName = inn.batting_team_name || inn.team_name || `Innings ${idx + 1}`;
            // Ball count: prefer the loaded balls list when this is the
            // active tab (frame-accurate), otherwise estimate from the
            // scorecard summary (legal balls + wickets is close enough
            // for a tab badge).
            const ballCount = isActive
              ? balls.length
              : ((inn.total_balls != null)
                  ? inn.total_balls
                  : ((inn.overs_completed ?? 0) * 6 + (inn.balls_in_current_over ?? 0)));
            return (
              <button
                key={inn.id}
                onClick={() => setActiveInnings(inn)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-button-primary/15 text-text-primary font-semibold border border-button-primary/40"
                    : "bg-bg-secondary text-text-secondary hover:text-text-primary border border-border-default"
                }`}
              >
                Innings {idx + 1} · {battingTeamName}
                {inn.total_runs != null && (
                  <span className="ml-2 text-[11px] text-text-secondary font-mono">
                    {inn.total_runs}/{inn.total_wickets ?? 0} ({inn.overs_completed ?? 0}.{inn.balls_in_current_over ?? 0})
                  </span>
                )}
                <span className="ml-2 text-[11px] text-text-secondary">· {ballCount} ball{ballCount === 1 ? "" : "s"}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-300 text-sm">
          This match has no innings yet — there's nothing to edit.
        </div>
      )}

      {/* Search + jump-to-over + refresh.
          Jump-to-over is structured navigation that complements the freetext
          filter: parse "5.3" / "12" → find the matching ball → scroll its
          row into view + flash highlight. Solves "scroll 150 balls to find
          ball 42 of innings 2" from the QC workflow. */}
      {activeInnings && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter — over, batter, bowler, wicket, commentary…"
              className="w-full bg-bg-primary border border-border-default rounded-md py-1.5 pl-9 pr-3 text-sm text-text-primary placeholder-text-secondary/60 focus:outline-none focus:border-button-primary"
            />
          </div>
          <JumpToOver balls={balls} />
          <button
            onClick={() => fetchBalls(activeInnings.id)}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-bg-secondary hover:bg-bg-main text-text-primary border border-border-default"
            title="Reload balls"
          >
            <RotateCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <span className="text-xs text-text-secondary">
            {ballsLoading ? "Loading…" : `${filteredBalls.length} of ${balls.length} ball${balls.length === 1 ? "" : "s"}`}
          </span>
        </div>
      )}

      {/* Ball table */}
      {activeInnings && (
        <div className="bg-bg-primary border border-border-default rounded-lg overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-bg-secondary/40 text-text-secondary text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-2 w-16">Ball</th>
                <th className="text-left p-2">Batter</th>
                <th className="text-left p-2">Bowler</th>
                <th className="text-left p-2 w-14">Runs</th>
                <th className="text-left p-2">Extras</th>
                <th className="text-left p-2">Wicket</th>
                <th className="text-left p-2 hidden md:table-cell">Commentary</th>
                <th className="text-right p-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {filteredBalls.map((b) => (
                <tr key={b.id} id={`ball-row-${(b.over_number || 1) - 1}-${b.ball_in_over}`} className="hover:bg-bg-secondary/30">
                  <td className="p-2 font-mono text-xs">
                    {formatBallReference(matchType, { overNumber: b.over_number, ballInOver: b.ball_in_over })}
                    {b.wide_runs > 0 && (
                      <span className="text-amber-400 ml-1" title="Wide">wd</span>
                    )}
                    {b.no_ball_runs > 0 && (
                      <span className="text-amber-400 ml-1" title="No-ball">nb</span>
                    )}
                  </td>
                  <td className="p-2">{playerName(b.striker_id || b.batter_id)}</td>
                  <td className="p-2">{playerName(b.bowler_id)}</td>
                  <td className="p-2 font-mono">{b.runs_off_bat ?? 0}</td>
                  <td className="p-2 text-xs text-text-secondary">
                    {b.extras_type ? `${b.extras_type}${b.extras_runs ? ` (${b.extras_runs})` : ""}` : "—"}
                  </td>
                  <td className="p-2 text-xs">
                    {b.wicket_type ? (
                      <span className="text-rose-300">{b.wicket_type}</span>
                    ) : (
                      <span className="text-text-secondary">—</span>
                    )}
                  </td>
                  <td className="p-2 text-xs text-text-secondary truncate max-w-md hidden md:table-cell" title={b.commentary || ""}>
                    {b.commentary ? `${b.commentary.slice(0, 80)}${b.commentary.length > 80 ? "…" : ""}` : "—"}
                  </td>
                  <td className="p-2 text-right">
                    <button
                      onClick={() => setEditingBallId(b.id)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25"
                    >
                      <Pencil className="w-3 h-3" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {!ballsLoading && filteredBalls.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-4 text-center text-text-secondary text-sm">
                    {filter ? "No balls match that filter." : "No balls saved in this innings yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <EditBallDialog
        open={!!editingBallId}
        onClose={() => setEditingBallId(null)}
        inningsId={activeInnings?.id}
        matchId={id}
        matchType={matchType}
        initialBallId={editingBallId}
        players={players}
        onSaved={(savedBallId) => {
          // CF-P0-05 — fire observe-correction if this ball had a
          // stashed AI suggestion (set on a prior session in BBP).
          // Best-effort; reads from localStorage so it works across
          // refresh + page boundaries. No-op when no stash exists.
          fireAiObserveOnSave({ ballId: savedBallId, matchId: id });
          if (activeInnings?.id) fetchBalls(activeInnings.id);
        }}
      />
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {children}
    </div>
  );
}

// Structured "jump to over.ball" navigation. Accepts:
//   - "5"     → first ball of over 5 (server stores over_number=6, ball_in_over=1)
//   - "5.3"   → over 5, ball 3
//   - "5.0"   → also normalized to ball 1
// Scrolls the matching row into view + flashes its background so the
// operator's eye lands on it without scanning. Quieter than highlighting
// "any ball mentioning 5" via the freetext filter.
function JumpToOver({ balls }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const submit = (e) => {
    e?.preventDefault?.();
    setErr("");
    const m = text.trim().match(/^(\d+)(?:[.\s-](\d+))?$/);
    if (!m) { setErr("Format: 5 or 5.3"); return; }
    const over = parseInt(m[1], 10);
    const ball = m[2] ? Math.max(1, parseInt(m[2], 10)) : 1;
    const target = balls.find(
      (b) => ((b.over_number || 1) - 1) === over && b.ball_in_over === ball,
    );
    if (!target) { setErr(`No ${over}.${ball} in this innings`); return; }
    const el = document.getElementById(`ball-row-${over}-${ball}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Brief amber flash so the eye locks on.
      el.classList.add("bg-amber-500/30");
      setTimeout(() => el.classList.remove("bg-amber-500/30"), 1400);
    }
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-1">
      <input
        value={text}
        onChange={(e) => { setText(e.target.value); if (err) setErr(""); }}
        placeholder="Jump to 5.3"
        className={`w-28 bg-bg-primary border rounded-md py-1.5 px-2 text-sm text-text-primary placeholder-text-secondary/60 focus:outline-none ${
          err ? "border-rose-500/60" : "border-border-default focus:border-button-primary"
        }`}
        title={err || "Jump to over.ball — try 5.3 or 12"}
      />
      <button
        type="submit"
        className="text-xs px-2 py-1.5 rounded bg-bg-secondary hover:bg-bg-main text-text-primary border border-border-default"
      >
        Go
      </button>
    </form>
  );
}
